// 3D dice (issue 1183): the layer. Off by default and switched on per person (View › 3D dice, `localStorage`
// `vtt.dice3d`, try/catch); `prefers-reduced-motion` keeps it off whatever is stored. Client-only, like the visual
// effects (issue 1164): `TableState` hands each frame it receives to `throwFromFrame`, so a roll plays only when this
// viewer was sent it — a private DM roll a player never receives plays nothing for them, by construction. The log line
// is the log's, at once; the dice are decoration over the board, drawn on a 2D canvas of their own (no library: the
// polyhedra and their landing table are `dice3d.ts`), and the canvas is idle and empty between throws.
// For the tests' and the e2e's eyes: `data-active` while dice are on screen, `data-landed` the faces they came to rest
// on (read as the log reads them: a d100's pair as one number), `data-played` the throws played, and the draw cost
// (`data-draw-p95`, ms of script per frame) and frame gaps (`data-gap-p95`) of the last throw.
import { useEffect, useRef, useState } from 'react';
import { FADE_MS, REST_MS, SOLIDS, TUMBLE_MS, apply, poseAt, readDice, seeded, type DiceThrow, type DieSides, type M3, type Tumble, type V3 } from './dice3d';

const KEY = 'vtt.dice3d';
const reducedMotion = (): boolean => { try { return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };
const settingListeners = new Set<(on: boolean) => void>();
/** Each person's own switch, kept in this browser: off by default, and always off where the system asks for less motion. */
export const dice3dSetting = {
  get(): boolean {
    if (reducedMotion()) return false;
    try { return localStorage.getItem(KEY) === '1'; } catch { return false; }
  },
  reduced: reducedMotion,
  set(on: boolean): void {
    try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* storage refused: this page only */ }
    memo = on;
    for (const l of settingListeners) l(on);
  },
  subscribe(l: (on: boolean) => void): () => void { settingListeners.add(l); return () => { settingListeners.delete(l); }; },
};
/** A choice made on a page whose storage refuses it still holds for the page. */
let memo: boolean | null = null;
const isOn = (): boolean => !reducedMotion() && (memo ?? dice3dSetting.get());

type Listener = (t: DiceThrow) => void;
const listeners = new Set<Listener>();
const seen = new Set<string>();
/** Throws from frames: each roll plays once, only while the setting is on; nothing else ever reaches the layer. */
export const dice3dBus = {
  play(t: DiceThrow | null): boolean {
    if (!t || !isOn() || seen.has(t.id)) return false;
    seen.add(t.id);
    if (seen.size > 200) seen.delete(seen.values().next().value as string);
    for (const l of listeners) l(t);
    return true;
  },
  subscribe(l: Listener): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
};

const COLOURS: Record<DieSides, [number, number, number]> = { 4: [46, 125, 50], 6: [183, 28, 28], 8: [21, 101, 192], 10: [106, 27, 154], 12: [230, 81, 0], 20: [180, 140, 20] };
const LABEL_SCALE: Record<DieSides, number> = { 4: 0.42, 6: 0.62, 8: 0.48, 10: 0.42, 12: 0.42, 20: 0.34 };
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

/** One die drawn at a pose: lit faces turned to the viewer, each with its number painted on it. */
export function drawDie(ctx: CanvasRenderingContext2D, sides: DieSides, m: M3, cx: number, cy: number, r: number, tens: boolean, alpha: number): void {
  const solid = SOLIDS[sides];
  const pts = solid.verts.map((v) => { const p = apply(m, v); return [cx + p[0] * r, cy - p[1] * r] as const; });
  const [cr, cg, cb] = COLOURS[sides];
  ctx.globalAlpha = alpha;
  for (const f of solid.faces) {
    const n = apply(m, f.normal);
    if (n[2] <= 0.01) continue;
    const light = 0.45 + 0.55 * n[2];
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.beginPath();
    f.verts.forEach((i, k) => { const p = pts[i]!; if (k) ctx.lineTo(p[0], p[1]); else ctx.moveTo(p[0], p[1]); });
    ctx.closePath();
    ctx.fillStyle = `rgb(${Math.round(cr * light)},${Math.round(cg * light)},${Math.round(cb * light)})`;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 1; ctx.stroke();
    if (n[2] < 0.3) continue;
    // The number lies in the face's plane: its x along the face's right, its y down the face.
    const up = apply(m, f.up); const right = apply(m, cross(f.up, f.normal)); const c = apply(m, f.centre);
    const k = (r * LABEL_SCALE[sides]) / 20;
    ctx.setTransform(right[0] * k, -right[1] * k, -up[0] * k, up[1] * k, cx + c[0] * r, cy - c[1] * r);
    const text = tens ? `${f.label}0` : f.label;
    ctx.fillStyle = '#fff8e1';
    ctx.fillText(sides >= 8 && (text === '6' || text === '9') ? `${text}.` : text, 0, 0);
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
}

interface Playing { t: DiceThrow; start: number; tumbles: Tumble[]; r: number }

const p95 = (xs: number[]): number => { if (!xs.length) return 0; const s = [...xs].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.floor(s.length * 0.95))]!; };

/** The layer over the board: a canvas sized to it, drawn only while a throw is on screen. */
export function Dice3DLayer() {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [landed, setLanded] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [played, setPlayed] = useState(0);
  const [cost, setCost] = useState<{ draw: number; gap: number } | null>(null);
  useEffect(() => {
    let raf = 0; let playing: Playing | null = null; let draws: number[] = []; let gaps: number[] = []; let last = 0;
    const frame = (now: number) => {
      const cv = canvas.current; const p = playing;
      if (!cv || !p) return;
      const t0 = performance.now();
      if (last) gaps.push(now - last);
      last = now;
      const ctx = cv.getContext('2d');
      const elapsed = now - p.start;
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, cv.width, cv.height);
        ctx.font = 'bold 20px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        const alpha = elapsed <= TUMBLE_MS + REST_MS ? 1 : Math.max(0, 1 - (elapsed - TUMBLE_MS - REST_MS) / FADE_MS);
        p.t.dice.forEach((d, i) => {
          const pose = poseAt(d.sides, d.face, p.tumbles[i]!, elapsed / TUMBLE_MS);
          drawDie(ctx, d.sides, pose.m, pose.x, pose.y - pose.lift * p.r * 2, p.r, d.tens === true, alpha);
        });
      }
      draws.push(performance.now() - t0);
      if (elapsed >= TUMBLE_MS && !cv.dataset.landed) { const v = readDice(p.t.dice).join(','); cv.dataset.landed = v; setLanded(v); }
      if (elapsed >= TUMBLE_MS + REST_MS + FADE_MS) {
        ctx?.clearRect(0, 0, cv.width, cv.height);
        playing = null; setActive(false); setCost({ draw: p95(draws), gap: p95(gaps) });
        return;
      }
      raf = requestAnimationFrame(frame);
    };
    const off = dice3dBus.subscribe((t) => {
      const cv = canvas.current; const box = host.current;
      if (!cv || !box) return;
      const w = Math.max(1, box.clientWidth); const h = Math.max(1, box.clientHeight);
      if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
      const r = Math.max(18, Math.min(40, w / (t.dice.length * 2.6 + 1)));
      const perRow = Math.max(1, Math.floor((w - r) / (r * 2.6)));
      const rows = Math.ceil(t.dice.length / perRow);
      const rnd = seeded(t.id);
      const tumbles = t.dice.map((_, i): Tumble => {
        const row = Math.floor(i / perRow); const inRow = Math.min(perRow, t.dice.length - row * perRow);
        const to = { x: w / 2 + ((i % perRow) - (inRow - 1) / 2) * r * 2.6, y: h / 2 + (row - (rows - 1) / 2) * r * 2.6 };
        return { axis: [rnd() - 0.5, rnd() - 0.5, rnd() - 0.5 + 0.01], turns: 2 + rnd() * 2, from: { x: -r * 2 - rnd() * w * 0.2, y: to.y + (rnd() - 0.5) * h * 0.4 }, to };
      });
      delete cv.dataset.landed;
      setLanded(null); setActive(true); setPlayed((n) => n + 1);
      draws = []; gaps = []; last = 0;
      playing = { t, start: performance.now(), tumbles, r };
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(frame);
    });
    return () => { off(); cancelAnimationFrame(raf); };
  }, []);
  return (
    <div ref={host} className="pointer-events-none absolute inset-0 z-20" data-testid="dice3d" data-active={active ? 'true' : 'false'} data-played={played}
      {...(landed ? { 'data-landed': landed } : {})} {...(cost ? { 'data-draw-p95': cost.draw.toFixed(1), 'data-gap-p95': cost.gap.toFixed(1) } : {})} aria-hidden="true">
      <canvas ref={canvas} className="h-full w-full" />
    </div>
  );
}
